import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './SuperAdmin.css';

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAdmins: 0,
    totalEmployers: 0,
    totalCandidates: 0,
    totalConsultants: 0,
    totalJobs: 0,
    totalApplications: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/super-admin/stats');
      // console.log('Stats response:', response);
      
      if (response && response.data) {
        setStats(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Stats error:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch statistics');
      toast.error('Failed to fetch statistics');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="super-admin-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="super-admin-dashboard">
        <div className="error-message">
          <h2>⚠️ Error Loading Dashboard</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchStats}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Super Admin Dashboard</h1>
          <p>Welcome back, {user?.name}!</p>
        </div>
        <button onClick={handleLogout} className="btn btn-danger">
          🚪 Logout
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon admin">👥</div>
          <div className="stat-info">
            <h3>{stats.totalAdmins}</h3>
            <p>Total Admins</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon employer">💼</div>
          <div className="stat-info">
            <h3>{stats.totalEmployers}</h3>
            <p>Total Employers</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon candidate">👤</div>
          <div className="stat-info">
            <h3>{stats.totalCandidates}</h3>
            <p>Total Candidates</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon consultant">🤝</div>
          <div className="stat-info">
            <h3>{stats.totalConsultants}</h3>
            <p>Total Consultants</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon jobs">📋</div>
          <div className="stat-info">
            <h3>{stats.totalJobs}</h3>
            <p>Total Jobs</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon applications">📝</div>
          <div className="stat-info">
            <h3>{stats.totalApplications}</h3>
            <p>Total Applications</p>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/super-admin/admins" className="action-card">
            <span className="action-icon">👥</span>
            <h3>Manage Admins</h3>
            <p>Create and manage admin users</p>
          </Link>

          <Link to="/super-admin/users" className="action-card">
            <span className="action-icon">👤</span>
            <h3>Manage Users</h3>
            <p>View and manage all users</p>
          </Link>

          <Link to="/super-admin/companies" className="action-card">
            <span className="action-icon">🏢</span>
            <h3>Manage Companies</h3>
            <p>View and manage companies</p>
          </Link>

          <Link to="/super-admin/settings" className="action-card">
            <span className="action-icon">⚙️</span>
            <h3>System Settings</h3>
            <p>Configure system settings</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
